import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import i18n from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios instance with credentials (for cookie auth)
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Anmeldung ausschließlich über httpOnly-Cookie; kein Token im Browser-Speicher
axios.defaults.withCredentials = true;
try { localStorage.removeItem('auth_token'); } catch (e) { /* ignore */ }
const ANSICHT_KEY = 'kp_portalansicht';
const leseAnsicht = () => { try { return JSON.parse(sessionStorage.getItem(ANSICHT_KEY) || 'null'); } catch (e) { return null; } };
api.interceptors.request.use((config) => {
  const ansicht = leseAnsicht();
  if (ansicht?.mandanten_nr) {
    config.headers = config.headers || {};
    config.headers['X-Portalansicht'] = ansicht.mandanten_nr;
    return config;
  }
  const mnr = localStorage.getItem('kp_mandant_ctx');
  if (mnr) {
    config.headers = config.headers || {};
    config.headers['X-Mandanten-Nr'] = mnr;
  }
  return config;
});

api.interceptors.response.use((r) => r, (err) => {
  if (err?.response?.status === 428 && err.response.data?.detail === 'RECHTSTEXTE_BESTAETIGEN') window.dispatchEvent(new Event('rechtstexte-pflicht'));
  if (err?.response?.status === 403 && err.response.data?.detail === '2FA_EINRICHTEN' && !window.location.pathname.startsWith('/profil')) window.location.assign('/profil?zwei_fa=pflicht');
  return Promise.reject(err);
});

axios.interceptors.request.use((config) => {
  const ansicht = leseAnsicht();
  if (ansicht?.mandanten_nr) { config.headers = config.headers || {}; config.headers['X-Portalansicht'] = ansicht.mandanten_nr; }
  return config;
});

const AuthContext = createContext({
  user: null,
  loading: true,
  isAuthenticated: false,
  isAdmin: false,
  isKanzlei: false,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ansicht, setAnsicht] = useState(leseAnsicht);
  const portalansichtStarten = useCallback((m) => { const a = { mandanten_nr: m.mandanten_nr, name: m.name || '', email: m.email || '' }; sessionStorage.setItem(ANSICHT_KEY, JSON.stringify(a)); setAnsicht(a); }, []);
  const portalansichtBeenden = useCallback(() => { sessionStorage.removeItem(ANSICHT_KEY); setAnsicht(null); }, []);
  const imAnsicht = !!ansicht && ['admin', 'kanzlei_mitarbeiter'].includes(user?.role);
  const sichtbarerUser = imAnsicht ? { user_id: `ansicht_${user.user_id}`, role: 'user', name: ansicht.name || `Mandant ${ansicht.mandanten_nr}`, email: ansicht.email, sprache: user.sprache,
    tour_erledigt_am: user.created_at || '2020-01-01', tipps_erledigt: true, ansicht: true } : user;
  // Track whether the initial /me probe has completed
  const initialCheckDone = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (e) {
      // Only wipe user on the very first probe. After that, NEVER auto-logout —
      // transient 401s (e.g., flaky network, hot-reload during dev) must not kick
      // the user back to /login when they have an active session.
      if (!initialCheckDone.current) {
        setUser(null);
      }
    } finally {
      initialCheckDone.current = true;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      initialCheckDone.current = true;  // OAuth flow is its own auth source
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.two_factor_required) return data;
    setUser(data.user);
    return data.user;
  };

  useEffect(() => {
    if (!user || user.role !== 'user' || user.ansicht) return undefined;
    const save = (lng) => { if (lng && (lng || '').slice(0, 2) !== user.sprache) api.post('/konto/sprache', { sprache: lng.slice(0, 2) }).catch(() => {}); };
    save(i18n.language);
    i18n.on('languageChanged', save);
    return () => i18n.off('languageChanged', save);
  }, [user]);

  const loginWithMagicLink = useCallback(async (token) => {
    const { data } = await api.post('/auth/magic-link/verify', { token });
    if (data.two_factor_required) return data;
    setUser(data.user);
    return data.user;
  }, []);

  const verify2fa = async (pendingToken, code, merken = false) => {
    const { data } = await api.post('/auth/2fa/verify', { pending_token: pendingToken, code, merken });
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post('/auth/register', { email, password, name });
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem('kp_mandant_ctx');
    sessionStorage.removeItem(ANSICHT_KEY);
    setAnsicht(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        loginWithMagicLink,
        user: sichtbarerUser,
        echterUser: user,
        portalansicht: imAnsicht ? ansicht : null,
        portalansichtStarten,
        portalansichtBeenden,
        loading,
        isAuthenticated: !!user,
        isAdmin: sichtbarerUser?.role === 'admin',
        isKanzlei: ['admin', 'kanzlei_mitarbeiter', 'freelancer'].includes(sichtbarerUser?.role),
        login,
        verify2fa,
        register,
        logout,
        refresh: checkAuth,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
